import { useEffect } from 'react';
import { useMatches } from 'react-router-dom';

import { APP_TITLE } from './constants';
import { useRouteEntityName } from './entityName';
import { RouteTitleHandle, buildDocumentTitle, resolveRouteTitleHandle } from './title';

/**
 * Keeps the browser tab title in sync with the current route.
 *
 * The static half of the title — section and action — is declared as `handle`
 * metadata on the route definition, so it lives next to the route it describes
 * and cannot drift from it. The entity name is published by the page; see
 * ./entityName.
 *
 * useMatches() is answered by the outer data router, so a page must be matched
 * by that router for its metadata to be reachable. Every route is registered
 * there; where a page still renders its own descendant <Routes> internally (see
 * @components/trackingPlans/trackingPlanV2View), the metadata comes from the
 * route that mounts it and is inherited by everything below.
 */
export const useDocumentTitle = (): void => {
  const matches = useMatches();
  const entityName = useRouteEntityName();

  const { section, action, subsection } = resolveRouteTitleHandle(
    matches.map((match) => match.handle as RouteTitleHandle | undefined),
  );

  const documentTitle = buildDocumentTitle({ section, action, subsection, entityName });

  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  // Unmount only — keyed to the title, this would reset to the bare app name
  // before every change.
  useEffect(
    () => () => {
      document.title = APP_TITLE;
    },
    [],
  );
};
